import { PrimeIcons } from '@primevue/core/api';
import type { ComputedRef, MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';

import { ActionCommand, RygenMenuItem } from '@rygen/rygen-ui/model';
import { Shipment as EsShipment } from '@rygen/tms-search-model/search/shipment';

import { ShipmentAction, ShipmentSearchData, ShipmentStatus } from '@/model';

interface useShipmentBatchActionResponse {
  availableActions: ComputedRef<RygenMenuItem[]>;
  allHaveSameStatus: ComputedRef<boolean>;
  status: ComputedRef<ShipmentStatus | undefined>;
  actions: ComputedRef<{ [shipmentAction in ShipmentAction]: RygenMenuItem }>;
  actionsByStatus: ComputedRef<{
    [shipmentStatus in ShipmentStatus]: RygenMenuItem[];
  }>;
}

export const useShipmentBatchAction = (
  selectedData: MaybeRefOrGetter<ShipmentSearchData[] | EsShipment[]>,
  emitCommand: (payload: ActionCommand<ShipmentAction>) => void,
): useShipmentBatchActionResponse => {
  const { t } = useI18n();

  const allHaveSameStatus = computed(() => {
    const shipments = toValue(selectedData);
    return shipments.every((s, _i, self) => s.status === self[0].status);
  });

  const status = computed(() => {
    const shipments = toValue(selectedData);
    if (!shipments?.length || !allHaveSameStatus.value) {
      return;
    }

    return shipments[0].status as ShipmentStatus;
  });

  const allHaveCarrierAssignedStatus = computed(() => {
    const shipments = toValue(selectedData);
    return shipments.every((s) => s.status === ShipmentStatus.CARRIER_ASSIGNED);
  });

  const allHaveSameClient = computed(() => {
    const shipments = toValue(selectedData);
    if (!shipments?.length) {
      return true;
    }

    if (ShipmentSearchData.isOldSearchArray(shipments)) {
      const firstClientNaturalId = shipments[0].client?.naturalId;
      return shipments.every(
        (s) => s.client?.naturalId === firstClientNaturalId,
      );
    } else {
      const firstClientNaturalId = shipments[0].owner?.naturalId;
      return shipments.every(
        (s) => s.owner?.naturalId === firstClientNaturalId,
      );
    }
  });

  const canBulkTender = computed(() => {
    return allHaveCarrierAssignedStatus.value && allHaveSameClient.value;
  });

  const createEmptyAction = (action: ShipmentAction) => ({
    key: action,
    label: '',
    command: () => emitCommand({ action }),
    visible: false,
  });

  const actions: ComputedRef<{
    [shipmentAction in ShipmentAction]: RygenMenuItem;
  }> = computed(() => {
    const shipments = toValue(selectedData);

    return {
      [ShipmentAction.ADD_TAGS]: {
        key: ShipmentAction.ADD_TAGS,
        label: t('common.addParam', { param: t('tags.tags', 2) }),
        icon: PrimeIcons.TAG,
        command: () => emitCommand({ action: ShipmentAction.ADD_TAGS }),
      },
      [ShipmentAction.CANCEL]: createEmptyAction(ShipmentAction.CANCEL),
      [ShipmentAction.CANCEL_CHECK_CALL_EMAILS]: {
        key: ShipmentAction.CANCEL_CHECK_CALL_EMAILS,
        label: t('common.cancelParam', {
          param: t('carrierPortal.checkCallReminders.checkCallReminders'),
        }),
        icon: 'pi pi-times',
        command: () =>
          emitCommand({ action: ShipmentAction.CANCEL_CHECK_CALL_EMAILS }),
        requiredAuthorization: ['shipment', 'update'],
      },
      [ShipmentAction.CANCEL_WATERFALL_TENDER]: createEmptyAction(
        ShipmentAction.CANCEL_WATERFALL_TENDER,
      ),
      [ShipmentAction.CARRIER_UNASSIGN]: {
        key: ShipmentAction.CARRIER_UNASSIGN,
        label: t('common.removeParam', {
          param: t('carriers.carriers', shipments.length),
        }),
        icon: PrimeIcons.TIMES,
        command: () =>
          emitCommand({
            action: ShipmentAction.CARRIER_UNASSIGN,
          }),
        requiredAuthorization: ['shipment', 'update'],
      },
      [ShipmentAction.CONSOLIDATE]: createEmptyAction(
        ShipmentAction.CONSOLIDATE,
      ),
      [ShipmentAction.EMAIL_DOCUMENTS]: createEmptyAction(
        ShipmentAction.EMAIL_DOCUMENTS,
      ),
      [ShipmentAction.EVENT_DELIVERED_CREATE]: {
        key: ShipmentAction.EVENT_DELIVERED_CREATE,
        label: t('shipments.addDelivery'),
        icon: PrimeIcons.CHECK,
        command: () =>
          emitCommand({
            action: ShipmentAction.EVENT_DELIVERED_CREATE,
          }),
        requiredAuthorization: ['shipmentEvent', 'update'],
      },
      [ShipmentAction.EVENT_DELIVERED_UNDO]: createEmptyAction(
        ShipmentAction.EVENT_DELIVERED_UNDO,
      ),
      [ShipmentAction.EVENT_MISC_CREATE]: createEmptyAction(
        ShipmentAction.EVENT_MISC_CREATE,
      ),
      [ShipmentAction.EVENT_PICKUP_CREATE]: {
        key: ShipmentAction.EVENT_PICKUP_CREATE,
        label: t('shipments.addPickup'),
        icon: PrimeIcons.PLUS,
        command: () =>
          emitCommand({ action: ShipmentAction.EVENT_PICKUP_CREATE }),
        requiredAuthorization: ['shipmentEvent', 'update'],
      },
      [ShipmentAction.EVENT_PICKUP_UNDO]: createEmptyAction(
        ShipmentAction.EVENT_PICKUP_UNDO,
      ),
      [ShipmentAction.EVENT_SERVICE_FAILURE_CREATE]: createEmptyAction(
        ShipmentAction.EVENT_SERVICE_FAILURE_CREATE,
      ),
      [ShipmentAction.INITIATE_MACROPOINT_TRACKING]: createEmptyAction(
        ShipmentAction.INITIATE_MACROPOINT_TRACKING,
      ),
      [ShipmentAction.PRINT_BOL]: {
        key: ShipmentAction.PRINT_BOL,
        label: t('shipments.printBol'),
        icon: PrimeIcons.PRINT,
        command: () => emitCommand({ action: ShipmentAction.PRINT_BOL }),
      },
      [ShipmentAction.PRINT_SHIPPING_LABEL_MODAL]: {
        key: ShipmentAction.PRINT_SHIPPING_LABEL_MODAL,
        label: t('shipments.printShippingLabel'),
        icon: PrimeIcons.PRINT,
        command: () =>
          emitCommand({
            action: ShipmentAction.PRINT_SHIPPING_LABEL_MODAL,
          }),
      },
      [ShipmentAction.CARGO_INSURANCE_CANCEL]: createEmptyAction(
        ShipmentAction.CARGO_INSURANCE_CANCEL,
      ),
      [ShipmentAction.CARGO_INSURANCE_PURCHASE]: createEmptyAction(
        ShipmentAction.CARGO_INSURANCE_PURCHASE,
      ),
      [ShipmentAction.REMOVE_ORDERS]: createEmptyAction(
        ShipmentAction.REMOVE_ORDERS,
      ),
      [ShipmentAction.REQUEST_DOCUMENTS]: createEmptyAction(
        ShipmentAction.REQUEST_DOCUMENTS,
      ),
      [ShipmentAction.REQUEST_SPOT_MARKET_BID]: {
        key: ShipmentAction.REQUEST_SPOT_MARKET_BID,
        label: t('carrierPortal.spotMarket.requestBid'),
        icon: PrimeIcons.DOLLAR,
        command: () =>
          emitCommand({ action: ShipmentAction.REQUEST_SPOT_MARKET_BID }),
      },
      [ShipmentAction.REQUEST_TRACKING]: createEmptyAction(
        ShipmentAction.REQUEST_TRACKING,
      ),
      [ShipmentAction.RESEND_CONFIRMATION]: createEmptyAction(
        ShipmentAction.RESEND_CONFIRMATION,
      ),
      [ShipmentAction.SEND_CHECK_CALL_REQUEST]: {
        key: ShipmentAction.SEND_CHECK_CALL_REQUEST,
        label: t('carrierPortal.checkCallReminders.send'),
        icon: PrimeIcons.SEND,
        command: () =>
          emitCommand({ action: ShipmentAction.SEND_CHECK_CALL_REQUEST }),
        requiredAuthorization: ['shipment', 'read'],
      },
      [ShipmentAction.SEND_TO_ROUTE_BUILDER]: createEmptyAction(
        ShipmentAction.SEND_TO_ROUTE_BUILDER,
      ),
      [ShipmentAction.SEND_TO_SHIPMENT_BUILDER]: createEmptyAction(
        ShipmentAction.SEND_TO_SHIPMENT_BUILDER,
      ),
      [ShipmentAction.SHARE]: createEmptyAction(ShipmentAction.SHARE),
      [ShipmentAction.TENDER_CONFIRM]: {
        key: ShipmentAction.TENDER_CONFIRM,
        label: t('shipments.confirmTender'),
        icon: PrimeIcons.CHECK,
        command: () => emitCommand({ action: ShipmentAction.TENDER_CONFIRM }),
        requiredAuthorization: ['shipment', 'update'],
      },
      [ShipmentAction.TENDER_REJECT]: {
        key: ShipmentAction.TENDER_REJECT,
        label: t('shipments.rejectTender'),
        icon: PrimeIcons.MINUS_CIRCLE,
        command: () => emitCommand({ action: ShipmentAction.TENDER_REJECT }),
        requiredAuthorization: ['shipment', 'update'],
      },
      [ShipmentAction.TENDER_RESEND]: createEmptyAction(
        ShipmentAction.TENDER_RESEND,
      ),
      [ShipmentAction.TENDER_TO_CARRIER]: {
        key: ShipmentAction.TENDER_TO_CARRIER,
        label: t('shipments.tender'),
        icon: PrimeIcons.SEND,
        command: () =>
          emitCommand({ action: ShipmentAction.TENDER_TO_CARRIER }),
        requiredAuthorization: ['shipment', 'update'],
        visible: canBulkTender.value,
      },
      [ShipmentAction.UPDATE_HEALTH]: {
        key: ShipmentAction.UPDATE_HEALTH,
        label: t('common.updateParam', { param: t('shipments.health') }),
        icon: PrimeIcons.HEART,
        command: () => emitCommand({ action: ShipmentAction.UPDATE_HEALTH }),
        requiredAuthorization: ['shipment', 'update'],
      },
      [ShipmentAction.WATERFALL_TENDER]: {
        key: ShipmentAction.WATERFALL_TENDER,
        label: t('waterfallTender.waterfallTender'),
        icon: PrimeIcons.LIST,
        command: () => emitCommand({ action: ShipmentAction.WATERFALL_TENDER }),
        requiredAuthorization: ['shipment', 'update'],
      },
      [ShipmentAction.ASSIGN_USERS]: {
        key: ShipmentAction.ASSIGN_USERS,
        label: t('common.assignParam', { param: t('users.users', 2) }),
        icon: PrimeIcons.USERS,
        command: () => emitCommand({ action: ShipmentAction.ASSIGN_USERS }),
        requiredAuthorization: ['shipment', 'update'],
      },
    };
  });

  const actionsByStatus: ComputedRef<{
    [shipmentStatus in ShipmentStatus]: RygenMenuItem[];
  }> = computed(() => ({
    [ShipmentStatus.PLANNING]: [
      actions.value[ShipmentAction.WATERFALL_TENDER],
      actions.value[ShipmentAction.ADD_TAGS],
      actions.value[ShipmentAction.UPDATE_HEALTH],
      actions.value[ShipmentAction.REQUEST_SPOT_MARKET_BID],
      { separator: true },
      actions.value[ShipmentAction.PRINT_SHIPPING_LABEL_MODAL],
      actions.value[ShipmentAction.ASSIGN_USERS],
    ],
    [ShipmentStatus.CARRIER_ASSIGNED]: [
      actions.value[ShipmentAction.CARRIER_UNASSIGN],
      actions.value[ShipmentAction.ADD_TAGS],
      actions.value[ShipmentAction.UPDATE_HEALTH],
      { separator: true },
      actions.value[ShipmentAction.PRINT_SHIPPING_LABEL_MODAL],
      actions.value[ShipmentAction.PRINT_BOL],
      actions.value[ShipmentAction.TENDER_TO_CARRIER],
      actions.value[ShipmentAction.ASSIGN_USERS],
    ],
    [ShipmentStatus.TENDERED]: [
      actions.value[ShipmentAction.TENDER_CONFIRM],
      actions.value[ShipmentAction.TENDER_REJECT],
      actions.value[ShipmentAction.CARRIER_UNASSIGN],
      actions.value[ShipmentAction.ADD_TAGS],
      actions.value[ShipmentAction.UPDATE_HEALTH],
      { separator: true },
      actions.value[ShipmentAction.PRINT_SHIPPING_LABEL_MODAL],
      actions.value[ShipmentAction.PRINT_BOL],
      actions.value[ShipmentAction.ASSIGN_USERS],
    ],
    [ShipmentStatus.TENDER_REJECTED]: [
      actions.value[ShipmentAction.CARRIER_UNASSIGN],
      actions.value[ShipmentAction.ADD_TAGS],
      actions.value[ShipmentAction.UPDATE_HEALTH],
      { separator: true },
      actions.value[ShipmentAction.PRINT_SHIPPING_LABEL_MODAL],
      actions.value[ShipmentAction.PRINT_BOL],
      actions.value[ShipmentAction.ASSIGN_USERS],
    ],
    [ShipmentStatus.BOOKED]: [
      actions.value[ShipmentAction.EVENT_PICKUP_CREATE],
      actions.value[ShipmentAction.CARRIER_UNASSIGN],
      actions.value[ShipmentAction.ADD_TAGS],
      actions.value[ShipmentAction.UPDATE_HEALTH],
      actions.value[ShipmentAction.SEND_CHECK_CALL_REQUEST],
      actions.value[ShipmentAction.CANCEL_CHECK_CALL_EMAILS],
      { separator: true },
      actions.value[ShipmentAction.PRINT_SHIPPING_LABEL_MODAL],
      actions.value[ShipmentAction.PRINT_BOL],
      actions.value[ShipmentAction.ASSIGN_USERS],
    ],
    [ShipmentStatus.IN_TRANSIT]: [
      actions.value[ShipmentAction.EVENT_DELIVERED_CREATE],
      actions.value[ShipmentAction.ADD_TAGS],
      actions.value[ShipmentAction.UPDATE_HEALTH],
      actions.value[ShipmentAction.SEND_CHECK_CALL_REQUEST],
      actions.value[ShipmentAction.CANCEL_CHECK_CALL_EMAILS],
      { separator: true },
      actions.value[ShipmentAction.PRINT_SHIPPING_LABEL_MODAL],
      actions.value[ShipmentAction.PRINT_BOL],
      actions.value[ShipmentAction.ASSIGN_USERS],
    ],
    [ShipmentStatus.DELIVERED]: [
      actions.value[ShipmentAction.PRINT_SHIPPING_LABEL_MODAL],
      actions.value[ShipmentAction.PRINT_BOL],
      actions.value[ShipmentAction.ADD_TAGS],
      actions.value[ShipmentAction.UPDATE_HEALTH],
      actions.value[ShipmentAction.ASSIGN_USERS],
    ],
    [ShipmentStatus.CANCELED]: [
      actions.value[ShipmentAction.ADD_TAGS],
      actions.value[ShipmentAction.ASSIGN_USERS],
    ],
    [ShipmentStatus.COMPLETE]: [
      actions.value[ShipmentAction.ADD_TAGS],
      actions.value[ShipmentAction.ASSIGN_USERS],
    ],
  }));

  const availableActions = computed(() => {
    if (allHaveSameStatus.value && status.value) {
      return actionsByStatus.value[status.value];
    }

    const shipments = toValue(selectedData);
    const statuses = shipments.map((s) => s.status as ShipmentStatus);
    const allAvailableActions = statuses
      .map((status) => actionsByStatus.value[status])
      .flat()
      .filter((action) => {
        // Filter out actions that aren't available in each of the selected statuses
        return statuses
          .map((status) => actionsByStatus.value[status])
          .every((actions) => actions.includes(action));
      });
    return Array.from(new Set(allAvailableActions));
  });

  return {
    availableActions,
    allHaveSameStatus,
    status,
    actions,
    actionsByStatus,
  };
};
